import React from "react";
import { formatCurrency } from "@west-tennessee-consulting/ui";
import { Client } from "@/lib/types/database";

interface ClientContractInfoProps {
  client: Client;
  variant?: "default" | "compact";
  showTitle?: boolean;
}

export const ClientContractInfo: React.FC<ClientContractInfoProps> = ({
  client,
  variant = "default",
  showTitle = true,
}) => {
  const isCompact = variant === "compact";

  // hourly_rate / monthly_rate are BigFloat: pg_graphql returns them as STRINGS
  // even though codegen types them `number`. Interpolating one raw renders an
  // unformatted "$150.00000" / "$1500"; `formatCurrency` parses the BigFloat and
  // returns null when it is unusable, which is also the render guard below — so a
  // non-numeric value no longer shows a bare "$".
  const hourlyRate = formatCurrency(client.companies?.hourly_rate);
  const monthlyRate = formatCurrency(client.companies?.monthly_rate);

  const content = (
    <div className={isCompact ? "space-y-1" : "space-y-3"}>
      {client.companies?.contract_type && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            {isCompact ? "Contract" : "Contract Type"}
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground capitalize`}>
            {client.companies.contract_type}
          </p>
        </div>
      )}
      {hourlyRate !== null && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            Hourly Rate
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {hourlyRate}
          </p>
        </div>
      )}
      {monthlyRate !== null && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            Monthly Rate
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {monthlyRate}
          </p>
        </div>
      )}
      {client.companies?.payment_terms && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            {isCompact ? "Payment Terms" : "Payment Terms"}
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {client.companies.payment_terms}
          </p>
        </div>
      )}
      {client.companies?.tax_id && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            {isCompact ? "Tax ID" : "Tax ID"}
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {client.companies.tax_id}
          </p>
        </div>
      )}
    </div>
  );

  if (!showTitle) {
    return content;
  }

  return (
    <div>
      <h2 className={`${isCompact ? "text-sm" : "text-lg"} font-semibold mb-4`}>
        {isCompact ? "Contract & Financial" : "Contract & Financial Information"}
      </h2>
      {content}
    </div>
  );
};
